import type { ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

import {
    formatDeclination,
    formatRightAscension,
    readSkyPointer
} from '@/lib/sky-pointer'

const TEXT_SELECTOR = 'input:not([type="range"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), textarea, [contenteditable="true"]'
const LINK_SELECTOR = 'a[href], button, [role="button"], [role="switch"], [role="tab"], [role="option"], label, select, summary, input[type="range"], input[type="checkbox"], input[type="radio"], input[type="submit"], [data-cursor="link"]'
const DRAG_SELECTOR = '[data-cursor="drag"]'

/**
 * A delicate crosshair inside a thin ring. Over any sky scene it reads out the
 * right ascension and declination beneath it; over links it locks on.
 */
export const ReticleCursor = (): ReactNode => {
    const [enabled, setEnabled] = useState(false)
    const rootRef = useRef<HTMLDivElement | null>(null)
    const ringRef = useRef<HTMLDivElement | null>(null)
    const coordsRef = useRef<HTMLDivElement | null>(null)
    const raRef = useRef<HTMLSpanElement | null>(null)
    const decRef = useRef<HTMLSpanElement | null>(null)

    useEffect(() => {
        const query = window.matchMedia('(hover: hover) and (pointer: fine)')
        const apply = () => setEnabled(query.matches)

        apply()
        query.addEventListener('change', apply)

        return () => query.removeEventListener('change', apply)
    }, [])

    useEffect(() => {
        const root = rootRef.current
        const ring = ringRef.current
        const coordsBox = coordsRef.current
        const raText = raRef.current
        const decText = decRef.current

        if (!enabled || !root || !ring || !coordsBox || !raText || !decText) return

        const html = document.documentElement
        let x = -200
        let y = -200
        let ringX = x
        let ringY = y
        let visible = false
        let target: Element | null = null
        let lastRa = ''
        let lastDec = ''
        let frame = 0

        html.classList.add('has-reticle')

        const handleMove = (event: PointerEvent) => {
            if (event.pointerType === 'touch') return

            x = event.clientX
            y = event.clientY
            target = event.target instanceof Element ? event.target : null

            if (!visible) {
                visible = true
                ringX = x
                ringY = y
                root.dataset.visible = 'true'
            }

            const mode = target?.closest(TEXT_SELECTOR)
                ? 'text'
                : target?.closest(LINK_SELECTOR)
                    ? 'link'
                    : target?.closest(DRAG_SELECTOR)
                        ? 'drag'
                        : 'default'

            if (root.dataset.mode !== mode) root.dataset.mode = mode
        }

        const handleDown = () => {
            root.dataset.pressed = 'true'
        }

        const handleUp = () => {
            root.dataset.pressed = 'false'
        }

        const handleLeave = () => {
            visible = false
            root.dataset.visible = 'false'
        }

        const tick = () => {
            ringX += (x - ringX) * 0.24
            ringY += (y - ringY) * 0.24

            root.style.transform = `translate3d(${x}px, ${y}px, 0)`
            ring.style.transform = `translate3d(${ringX - x}px, ${ringY - y}px, 0)`

            const coordinates = visible ? readSkyPointer(target, x, y) : null

            if (coordinates) {
                const ra = `RA  ${formatRightAscension(coordinates.ra)}`
                const dec = `DEC ${formatDeclination(coordinates.dec)}`

                if (ra !== lastRa) {
                    raText.textContent = ra
                    lastRa = ra
                }

                if (dec !== lastDec) {
                    decText.textContent = dec
                    lastDec = dec
                }

                if (coordsBox.dataset.on !== 'true') coordsBox.dataset.on = 'true'
            }
            else if (coordsBox.dataset.on !== 'false') {
                coordsBox.dataset.on = 'false'
            }

            frame = requestAnimationFrame(tick)
        }

        window.addEventListener('pointermove', handleMove, { passive: true })
        window.addEventListener('pointerdown', handleDown, { passive: true })
        window.addEventListener('pointerup', handleUp, { passive: true })
        html.addEventListener('mouseleave', handleLeave)
        frame = requestAnimationFrame(tick)

        return () => {
            cancelAnimationFrame(frame)
            html.classList.remove('has-reticle')
            window.removeEventListener('pointermove', handleMove)
            window.removeEventListener('pointerdown', handleDown)
            window.removeEventListener('pointerup', handleUp)
            html.removeEventListener('mouseleave', handleLeave)
        }
    }, [enabled])

    if (!enabled) return null

    return (
        <div ref={rootRef} aria-hidden className='reticle' data-mode='default' data-visible='false' data-pressed='false'>
            <div ref={ringRef} className='reticle-ring-wrap'>
                <div className='reticle-ring' />
            </div>

            <svg className='reticle-cross' viewBox='0 0 18 18'>
                <line x1='9' y1='0' x2='9' y2='6' />
                <line x1='9' y1='12' x2='9' y2='18' />
                <line x1='0' y1='9' x2='6' y2='9' />
                <line x1='12' y1='9' x2='18' y2='9' />
                <circle cx='9' cy='9' r='0.9' />
            </svg>

            <svg className='reticle-lock' viewBox='0 0 22 22'>
                <path d='M1 7 V1 H7' />
                <path d='M15 1 H21 V7' />
                <path d='M21 15 V21 H15' />
                <path d='M7 21 H1 V15' />
            </svg>

            <div ref={coordsRef} className='reticle-coords' data-on='false'>
                <span ref={raRef} className='block' />
                <span ref={decRef} className='block' />
            </div>
        </div>
    )
}
